import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DeviceCameraInfo, DeviceCheckCriteria, DeviceRegistration, DeviceRegistrationTrustInfo, GatewayCommand, VirtualDevice, WaterSchedule, standardGetHttpOptions, standardHttpOptions } from '@models';
import { LoadingService } from '@services/loading.service';
import { WaterScheduleInfo } from '@models';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { Device } from '@models';
import { DeviceInfo } from '@models';
import { DeviceFunction } from '@models';
import { TaskInfo } from '@models';
import { Task } from '@models';
import { HttpHeaders } from '@angular/common/http';
import { AccessTaskParams } from '@models/Task/AccessTaskParams.model';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { UserService } from '@services/user.service';
import { retry, timeout, catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonShared } from '../classes/common';
import { IrrigationStatsCriteria } from '@models/Device/IrrigationStatsCriteria.model';


@Injectable()
export class DeviceService {

  public standardHttpOptionsWithUser: any;
  private apiUrl: string;
  private common = new CommonShared();

  // Cached variables
  public devices: DeviceInfo[] = [];
  public waterSchedules: WaterScheduleInfo[] = [];

  // Loaded flags let an empty result still count as cached; the in-flight observables let simultaneous callers share one request
  private devicesLoaded = false;
  private devicesInFlight: Observable<DeviceInfo[]> | null = null;
  private waterSchedulesLoaded = false;
  private waterSchedulesInFlight: Observable<WaterScheduleInfo[]> | null = null;

  // Reference data that rarely changes; kept for the life of the session
  private deviceFunctions: DeviceFunction[] | null = null;
  private deviceFunctionsInFlight: Observable<DeviceFunction[]> | null = null;
  private gatewayQuickLinkCommands: GatewayCommand[] | null = null;
  private gatewayQuickLinkCommandsInFlight: Observable<GatewayCommand[]> | null = null;


  constructor(private http: HttpClient, private loadingService: LoadingService, private userService: UserService,
              @Inject(KIG_SETTINGS) private settings: KigSettings) {

    this.apiUrl = this.settings.apiUrl;
  }


  public clearCachedVariables() {
    this.devices = [];
    this.devicesLoaded = false;
    this.devicesInFlight = null;
    this.clearWaterSchedulesCache();
  }


  public clearWaterSchedulesCache() {
    this.waterSchedules = [];
    this.waterSchedulesLoaded = false;
    this.waterSchedulesInFlight = null;
  }


  // Replaces (or adds) one gateway in the cache so a save doesn't require reloading every gateway
  public upsertCachedDevice(deviceInfo: DeviceInfo) {
    if (!this.devicesLoaded) {
      return;
    }

    const index = this.devices.findIndex(d => d.id === deviceInfo.id);
    if (index > -1) {
      this.devices[index] = deviceInfo;
    } else {
      this.devices.push(deviceInfo);
    }
    this.common.sortArrayByNameParm(this.devices);
  }


  public removeCachedDevice(id: number) {
    if (!this.devicesLoaded) {
      return;
    }

    this.devices = this.devices.filter(d => d.id !== id);
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getTasks(accessTaskParams: AccessTaskParams): Observable<any> {
    return this.http.post<TaskInfo[]>(`${ this.apiUrl }/task/get`, accessTaskParams, standardHttpOptions).pipe(
      retry(1),
      timeout(29000),
      catchError(this.handleError)
    );
  }


  public updateDeviceCamera(deviceCamera: DeviceCameraInfo): Observable<any> {
    return this.http.post<TaskInfo[]>(`${ this.apiUrl }/device/devicecamera`, deviceCamera, standardHttpOptions).pipe(
      retry(1),
      timeout(29000),
      catchError(this.handleError)
    );
  }


  public postTask(task: Task, loadMessage: string, retryCount: number): Observable<any> {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    const taskHttpOptions = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json'
      }), responseType: 'text' as 'json'
    };
    return this.http.post<string>(`${ this.apiUrl }/task/create`, task, taskHttpOptions).pipe(
      retry(retryCount),
      timeout(retryCount * 29000 - 2000),
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public getScoutviewImageAsBase64(fileName: string, loadMessage: string): Observable<any> {
    if (loadMessage != null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.get(`${ this.apiUrl }/device/ScoutviewImageAsBase64/${fileName}`, {responseType:'text'}).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage != null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public loadGatewayQuickLinkCommands(): Observable<any> {
    if (this.gatewayQuickLinkCommands != null) {
      return of(this.gatewayQuickLinkCommands.map(x => ({ ...x })));
    }

    if (this.gatewayQuickLinkCommandsInFlight == null) {
      this.loadingService.setMessage('Loading gateway commands ...');
      this.gatewayQuickLinkCommandsInFlight = this.http.get<GatewayCommand[]>(`${ this.apiUrl }/device/gatewaycommands`, standardGetHttpOptions).pipe(
        tap((response: GatewayCommand[]) => this.gatewayQuickLinkCommands = response?.map(x => ({ ...x })) ?? null),
        catchError(this.handleError),
        finalize(() => {
          this.gatewayQuickLinkCommandsInFlight = null;
          this.loadingService.clearMessage();
        }),
        shareReplay(1)
      );
    }
    return this.gatewayQuickLinkCommandsInFlight;
  }


  public getDevices(): Observable<any> {
    if (this.devicesLoaded) {
      //console.log('cached devices');
      return of(this.devices);
    }

    if (this.devicesInFlight == null) {
      this.loadingService.setMessage('Loading gateways ...');
      this.devicesInFlight = this.http.get<DeviceInfo[]>(`${ this.apiUrl }/device`, standardGetHttpOptions).pipe(
        map((response: DeviceInfo[]) => {
          //console.log('fetched devices');
          this.devices = response ?? [];
          this.devicesLoaded = response != null;
          this.common.sortArrayByNameParm(this.devices);
          return response == null ? null : this.devices;
        }),
        catchError(this.handleError),
        finalize(() => {
          this.devicesInFlight = null;
          this.loadingService.clearMessage();
        }),
        shareReplay(1)
      );
    }
    return this.devicesInFlight;
  }


  public getDevice(id: number): Observable<DeviceInfo> {
    return this.http.get<DeviceInfo>(`${ this.apiUrl }/device/${ id }`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getListOfImagesForScoutviewDevice(serialNumber: string): Observable<any> {
    this.loadingService.setMessage('Loading image list ...');
    return this.http.get<string[]>(`${ this.apiUrl }/device/ScoutviewImageList/${serialNumber}`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public removeDevice(id: number, username: string): Observable<number> {
    this.loadingService.setMessage('Removing gateway ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete<number>(`${ this.apiUrl }/device/${ id }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public addDevice(deviceInfo: DeviceInfo) {
    this.loadingService.setMessage('Creating gateway ...');
    return this.http.post(`${ this.apiUrl }/device`, deviceInfo, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateDevice(deviceInfo: DeviceInfo, username: string): Observable<Device> {
    this.loadingService.setMessage('Updating gateway ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${ this.apiUrl }/device`, deviceInfo, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getDeviceFunctions(): Observable<DeviceFunction[]> {
    if (this.deviceFunctions != null) {
      return of(this.deviceFunctions.map(x => ({ ...x })));
    }

    if (this.deviceFunctionsInFlight == null) {
      this.deviceFunctionsInFlight = this.http.get<DeviceFunction[]>(`${ this.apiUrl }/devicefunction`, standardHttpOptions).pipe(
        retry(1),
        timeout(29000),
        tap((response: DeviceFunction[]) => this.deviceFunctions = response?.map(x => ({ ...x })) ?? null),
        catchError(this.handleError),
        finalize(() => this.deviceFunctionsInFlight = null),
        shareReplay(1)
      );
    }
    return this.deviceFunctionsInFlight;
  }


  public getDeviceConnectivityStats(dataCriteria: DeviceCheckCriteria) {
    return this.http.post(`${ this.apiUrl }/device/ConnectivityStats`, dataCriteria, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public getDeviceIngestStats(dataCriteria: DeviceCheckCriteria) {
    return this.http.post(`${ this.apiUrl }/device/IngestStats`, dataCriteria, standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public getGlobalWaterSchedules(): Observable<any> {
    if (this.waterSchedulesLoaded) {
      //console.log('cached waterSchedules');
      return of(this.waterSchedules);
    }

    if (this.waterSchedulesInFlight == null) {
      this.loadingService.setMessage('Retrieving shared schedules from the server ...');
      this.waterSchedulesInFlight = this.http.get<WaterScheduleInfo[]>(`${this.apiUrl}/task/allwaterschedules`, standardGetHttpOptions).pipe(
        map((response: WaterScheduleInfo[]) => {
          //console.log('fetched waterSchedules');
          this.waterSchedules = response ?? [];
          this.waterSchedulesLoaded = response != null;
          this.common.sortArrayByNameParm(this.waterSchedules);
          return response == null ? null : this.waterSchedules;
        }),
        retry(2),
        timeout(25000),
        catchError(this.handleError),
        finalize(() => {
          this.waterSchedulesInFlight = null;
          this.loadingService.clearMessage();
        }),
        shareReplay(1)
      );
    }
    return this.waterSchedulesInFlight;
  }


  public getRegistrationInfoForDevices(serialNumbers: string[]): Observable<DeviceRegistration[]> {
    return this.http.post<DeviceRegistration[]>(`${ this.apiUrl }/deviceregistration/registrationinfo`, serialNumbers, standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public registerDevice(serialNumber: string, tokenType: string = 'Es256'): Observable<VirtualDevice> {
    return this.http.post<VirtualDevice>(`${ this.apiUrl }/deviceregistration/newvirtual/${serialNumber}?tokenType=${tokenType}`, '', standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public trustDevices(registrationTrustInfo: DeviceRegistrationTrustInfo): Observable<any> {
    this.loadingService.setMessage('Setting up trust ...');
    return this.http.post(`${ this.apiUrl }/deviceregistration/trusted`, registrationTrustInfo, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public unTrustDevices(registrationTrustInfo: DeviceRegistrationTrustInfo): Observable<any> {
    this.loadingService.setMessage('Removing trust ...');
    return this.http.post(`${ this.apiUrl }/deviceregistration/untrusted`, registrationTrustInfo, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateDeviceRegistration(serialNumber: string, tokenType: string = 'Es256'): Observable<VirtualDevice> {
    return this.http.post<VirtualDevice>(`${ this.apiUrl }/deviceregistration/updatevirtual/${serialNumber}?tokenType=${tokenType}`, '', standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public removeDeviceRegistration(serialNumber: string): Observable<VirtualDevice> {
    return this.http.post<VirtualDevice>(`${ this.apiUrl }/deviceregistration/remove/${serialNumber}`, '', standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getGatewayActivityMessages(sourceId: string, messageCount: number): Observable<any> {
    //this.loadingService.setMessage('Getting messages ...');
    return this.http.get(`${ this.apiUrl }/device/updateMessages/${sourceId}/${messageCount}`, { withCredentials: true }).pipe(
      catchError(this.handleError),
      //finalize(() => this.loadingService.clearMessage())
    );
  }


  public getGatewayActivityMessagesByDateRange(criteria: { sourceId: string; startDate: Date; endDate: Date; }): Observable<any> {
    return this.http.post(`${ this.apiUrl }/device/updateMessages/betweendates`, criteria, standardHttpOptions).pipe(
      catchError(this.handleError),
    );
  }


  public getIrrigationStats(criteria: IrrigationStatsCriteria): Observable<any> {
    this.loadingService.setMessage('Calculating stats ...');
    return this.http.post(`${ this.apiUrl }/device/irrigationstats`, criteria, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public postGatewayUpdateMessage(sourceId: string, message: string): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requestOptions: Object = {
      headers: headers,
      responseType: 'text'
    }

    return this.http.post(`${ this.apiUrl }/device/postMessage/${sourceId}`, JSON.stringify(message), requestOptions).pipe(
      catchError(this.handleError),
    );
  }


  public startDirectConnection(deviceId: number): Observable<any> {
    return this.http.get(`${ this.apiUrl }/device/startiotdirect/${deviceId}`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public deleteGatewayActivityMessages(messageIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/device/updateMessages/delete`, messageIds, standardHttpOptions).pipe(
      catchError(this.handleError),
    );
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
